/**
 * Module-level access-token accessor.
 *
 * The GraphQL client and the asset loader both need an Auth0 bearer token, but
 * neither runs inside React's render tree, so neither can call `useAuth0()`.
 * The app registers Auth0's `getAccessTokenSilently` here once at startup and
 * everything else pulls from this accessor.
 *
 * Registration happens in the app's AuthProvider; until then `getAccessToken`
 * resolves to `undefined` rather than throwing, so a request that races startup
 * fails as a normal 401 instead of an unhandled rejection.
 */
export type AccessTokenProvider = () => Promise<string | undefined>;

let provider: AccessTokenProvider = async () => undefined;

export function setAccessTokenProvider(next: AccessTokenProvider): void {
  provider = next;
}

export function getAccessToken(): Promise<string | undefined> {
  return provider();
}

/**
 * Decodes a JWT payload without verifying the signature.
 *
 * Only ever used to read claims from a token this client just received from
 * Auth0 — the API is what actually validates it. Mirrors the decoding the
 * former `/api/auth/identity` route did on the ALB's OIDC data header.
 */
export function decodeJwtPayload<T = Record<string, any>>(
  jwt: string | undefined,
): T | undefined {
  if (!jwt) return undefined;

  const segments = jwt.split(".");
  if (segments.length < 2) return undefined;

  try {
    const base64 = segments[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    // atob yields a binary string; round-tripping through escape/decodeURIComponent
    // would mangle multi-byte claims (e.g. a name with an accent), so decode the
    // raw bytes as UTF-8 instead.
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as T;
  } catch {
    return undefined;
  }
}
